/* kz-creator-videos (#5439, #6554, #6555): behaviour of snippets/kz-creator-videos.liquid for all three layouts.
   - all: ONE product switch (#6585, snippets/kz-creator-videos-products-switch: role=tablist, click, arrow keys /
     Home / End). Videos carry data-kzcv-groups="|handle|...|"; a tab ("all" = every video) shows on the shelf /
     library the cards of its product, on the product page its featured video, playlist and heading;
   - shelf / library: search (library), "Load more" (library), shelf arrows;
   - product: the playlist switches the featured video;
   - all: the player <dialog> (snippets/kz-creator-videos-dialog, also opened by other sections, e.g. kz-eon-tough;
     title, description, creator, product, prev / next; opens at 50% volume), Esc and backdrop close it,
     focus goes back to the button that opened it. Video data comes from <script type="application/json" data-kzcv-data>.
   The pure logic (match, countLabel, inGroup, tabKey, step) is exported for the unit test (tools/creator-videos.test.mjs). */
(() => {
  const api = {
    // True when an item ({ groups, search }) belongs to the selected product (null / 'all' = every product)
    // and passes the search query (case-insensitive substring of the item's search text).
    match(item, group, query) {
      const q = String(query || '').trim().toLowerCase();
      if (!api.inGroup(item.groups, group)) return false;
      return !q || String(item.search || '').toLowerCase().includes(q);
    },
    // "1 video" / "3 videos" (+ " found" while searching)
    countLabel(count, searching) {
      return `${count} ${count === 1 ? 'video' : 'videos'}${searching ? ' found' : ''}`;
    },
    // True when a video with these products ('|pro|rust|') belongs to the product tab (no tab / 'all' = all videos)
    inGroup(groups, group) {
      return !group || group === 'all' || String(groups || '').includes(`|${group}|`);
    },
    // The tab selected at start: the one rendered with aria-selected="true", else the first (index; -1 = no tabs)
    initialTab(selected) {
      if (!selected.length) return -1;
      const i = selected.indexOf(true);
      return i === -1 ? 0 : i;
    },
    // Index of the tab a key moves to (arrows wrap, Home / End), or -1 for any other key
    tabKey(key, index, count) {
      if (key === 'ArrowRight' || key === 'ArrowDown') return (index + 1) % count;
      if (key === 'ArrowLeft' || key === 'ArrowUp') return (index - 1 + count) % count;
      if (key === 'Home') return 0;
      if (key === 'End') return count - 1;
      return -1;
    },
    // Player prev / next inside the shown videos (order = their data indexes): the index `delta` steps from `active`
    step(order, active, delta) {
      if (!order.length) return active;
      const i = order.indexOf(active);
      return order[((i === -1 ? 0 : i + delta) % order.length + order.length) % order.length];
    },
    // True when the player has nothing to show next to the video (#6646): no description, creator, product, small print
    // and no prev / next (count = videos the player steps through) — e.g. the EON video of kz-eon-tough; the title alone
    // does not count. Then the info panel is not shown and the popup is the video only.
    bare(v, count, note) {
      return !v.description && !v.creator && !v.label && !v.product_url && !note && count < 2;
    },
  };
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  if (window.KzCreatorVideos) return;
  window.KzCreatorVideos = api;

  function init(root) {
    if (root.kzcvReady) return;
    root.kzcvReady = true;
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => [...root.querySelectorAll(sel)];
    let data = [];
    try { data = JSON.parse($('[data-kzcv-data]').textContent); } catch (e) { data = []; }

    // ---- shelf / library: cards, search, load more, shelf arrows ----
    const list = $('[data-kzcv-list]');
    const items = $$('[data-kzcv-item]').map((el) => ({ el, groups: el.dataset.kzcvGroups, search: el.dataset.search }));
    const perPage = Number(root.dataset.perPage) || 0;
    const search = $('[data-kzcv-search]');
    const count = $('[data-kzcv-count]');
    const empty = $('[data-kzcv-empty]');
    const more = $('[data-kzcv-more]');
    const arrows = $('[data-kzcv-arrows]');
    const state = { group: 'all', query: '', limit: perPage };

    function updateArrows() {
      if (!arrows || !list) return;
      const overflowing = list.scrollWidth > list.clientWidth + 2;
      arrows.hidden = !overflowing;
      arrows.querySelector('[data-kzcv-scroll="-1"]').disabled = list.scrollLeft < 2;
      arrows.querySelector('[data-kzcv-scroll="1"]').disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
    }

    function render() {
      const shown = items.filter((it) => api.match(it, state.group, state.query));
      items.forEach((it) => { it.el.hidden = true; });
      shown.forEach((it, i) => { it.el.hidden = Boolean(state.limit) && i >= state.limit; });
      if (count) count.textContent = api.countLabel(shown.length, Boolean(state.query.trim()));
      if (empty) empty.hidden = shown.length > 0;
      if (more) more.hidden = !state.limit || shown.length <= state.limit;
      requestAnimationFrame(updateArrows);
    }

    if (search) search.addEventListener('input', () => { state.query = search.value; state.limit = perPage; render(); });
    if (more) more.addEventListener('click', () => { state.limit += perPage; render(); });
    if (arrows && list) {
      list.addEventListener('scroll', updateArrows, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(updateArrows).observe(list);
      $$('[data-kzcv-scroll]').forEach((b) => b.addEventListener('click', () => {
        list.scrollBy({ left: Number(b.dataset.kzcvScroll) * list.clientWidth * 0.8, behavior: 'smooth' });
      }));
    }
    // ---- product layout: playlist selects the featured video ----
    const rows = $$('[data-kzcv-select]');
    function selectRow(row) {
      const n = row.dataset.kzcvSelect;
      $$('[data-kzcv-featured]').forEach((f) => { f.hidden = f.dataset.kzcvFeatured !== n; });
      rows.forEach((r) => {
        const on = r === row;
        r.classList.toggle('is-active', on);
        r.setAttribute('aria-pressed', String(on));
      });
    }
    rows.forEach((row) => row.addEventListener('click', () => selectRow(row)));

    // ---- product switch (all layouts) ----
    const tabs = $$('[data-kzcv-tab]');
    const panel = $('[data-kzcv-panel]');
    const playlistCount = $('[data-kzcv-playlist-count]');
    function selectTab(tab) {
      const group = tab.dataset.kzcvTab;
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      if (panel) panel.setAttribute('aria-labelledby', tab.id);
      // shelf / library: the cards of the product (with the search)
      if (items.length) {
        state.group = group;
        state.limit = perPage;
        render();
        if (list) list.scrollLeft = 0;
      }
      // product page: heading, playlist, count and featured video of the product
      if (rows.length) {
        $$('[data-kzcv-group-head]').forEach((h) => { h.hidden = h.dataset.kzcvGroupHead !== group; });
        const shown = rows.filter((r) => api.inGroup(r.dataset.kzcvGroups, group));
        rows.forEach((r) => { r.hidden = !shown.includes(r); });
        if (playlistCount) playlistCount.textContent = api.countLabel(shown.length, false);
        if (shown[0]) selectRow(shown[0]);
      }
    }
    const start = api.initialTab(tabs.map((t) => t.getAttribute('aria-selected') === 'true'));
    if (start >= 0) state.group = tabs[start].dataset.kzcvTab;
    if (items.length) render();
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => selectTab(tab));
      tab.addEventListener('keydown', (e) => {
        const to = api.tabKey(e.key, i, tabs.length);
        if (to < 0) return;
        e.preventDefault();
        tabs[to].focus();
        selectTab(tabs[to]);
      });
    });

    // ---- player dialog ----
    const dialog = $('[data-kzcv-dialog]');
    if (!dialog || !data.length) return;
    const video = dialog.querySelector('[data-kzcv-video]');
    const f = (name) => dialog.querySelector(`[data-kzcv-f="${name}"]`);
    let active = 0;
    let opener = null;
    // videos the player steps through: the playlist rows of the selected tab (product layout), else all
    const order = () => (rows.length ? rows.filter((r) => !r.hidden).map((r) => Number(r.dataset.kzcvSelect)) : data.map((v, i) => i));

    function show(index) {
      active = (index + data.length) % data.length;
      const v = data[active];
      const shown = order();
      f('title').textContent = v.title;
      f('description').textContent = v.description || '';
      f('description').hidden = !v.description;
      f('creator').innerHTML = v.creator || '';
      // no product in the data (e.g. a video on its own product page, #6586) → no product block
      f('product').hidden = !v.label;
      f('label').textContent = v.label || '';
      f('price').textContent = v.price || '';
      const img = f('product_image');
      img.hidden = !v.product_image;
      if (v.product_image) { img.src = v.product_image; img.alt = v.label; }
      const cta = f('product_url');
      cta.hidden = !v.product_url;
      cta.href = v.product_url || '#';
      f('position').textContent = `${Math.max(shown.indexOf(active), 0) + 1} / ${shown.length}`;
      dialog.querySelector('[data-kzcv-next]').hidden = shown.length < 2;
      // nothing for the info panel (#6646) → video only; the title stays in the hidden panel for aria-labelledby
      const bare = api.bare(v, shown.length, dialog.querySelector('.kzcv-note'));
      dialog.classList.toggle('kzcv-dialog--bare', bare);
      dialog.querySelector('.kzcv-player__info').hidden = bare;
      video.pause();
      video.poster = v.poster || '';
      video.src = v.src;
      if (!dialog.open) {
        opener = document.activeElement;
        // every opening plays at 50% volume, not 100% (#6586); prev / next inside the open player keep what the viewer chose
        video.volume = 0.5;
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }

    function close() {
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (dialog.open) dialog.close();
      document.documentElement.style.overflow = '';
      if (opener && opener.focus) opener.focus();
    }

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-kzcv-play]');
      if (btn && root.contains(btn)) show(Number(btn.dataset.kzcvPlay));
    });
    dialog.querySelector('[data-kzcv-close]').addEventListener('click', close);
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dialog.addEventListener('click', (e) => {
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
    });
    dialog.querySelectorAll('[data-kzcv-step]').forEach((b) => b.addEventListener('click', () => show(api.step(order(), active, Number(b.dataset.kzcvStep)))));
  }

  const boot = (scope) => scope.querySelectorAll('[data-kzcv]').forEach(init);
  boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
